import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import './AdminDashboard.css';

const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

function PartnerDashboardPage() {
  const { t, i18n } = useTranslation();
  const { instructor } = useAuth();
  const locale = i18n.language === 'hu' ? 'hu-HU' : i18n.language === 'he' ? 'he-IL' : 'en-GB';
  const [partner, setPartner] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadPartnerData() {
      try {
        setLoading(true);
        setError('');
        const [meRes, bookingsRes] = await Promise.all([
          axios.get('/api/partners/me'),
          axios.get('/api/partners/bookings')
        ]);
        if (cancelled) return;
        setPartner(meRes.data.partner || null);
        setBookings(bookingsRes.data.bookings || []);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || t('partnerDashboard.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadPartnerData();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const formatDate = (date) => {
    try {
      return new Date(date).toLocaleDateString(locale);
    } catch {
      return date;
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">{t('partnerDashboard.loading')}</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div>
          <h1>{t('partnerDashboard.title')}</h1>
          <p>{partner?.campaignName || instructor?.name} · {t('partnerDashboard.bookings')}</p>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="dashboard-section">
        <div className="card">
          <h3>{t('partnerDashboard.bookings')}</h3>
          {bookings.length === 0 ? (
            <p>{t('partnerDashboard.empty')}</p>
          ) : (
            <div className="caa-overview-table-wrap">
              <table className="caa-overview-table">
                <thead>
                  <tr>
                    <th>{t('partnerDashboard.createdAt')}</th>
                    <th>{t('partnerDashboard.flightAt')}</th>
                    <th>{t('partnerDashboard.customer')}</th>
                    <th>{t('partnerDashboard.passengers')}</th>
                    <th>{t('partnerDashboard.amount')}</th>
                    <th>{t('partnerDashboard.paymentStatus')}</th>
                    <th>{t('partnerDashboard.coupon')}</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id}>
                      <td>{booking.created_at ? new Date(booking.created_at).toLocaleString(locale) : '-'}</td>
                      <td>{formatDate(booking.date)} {formatHour(booking.hour)}</td>
                      <td>
                        <strong>{booking.customer_name}</strong>
                        <br />
                        <span>{booking.customer_email}</span>
                      </td>
                      <td>{booking.passenger_count || 1}</td>
                      <td>{Number(booking.payment_amount || 0).toLocaleString(locale)} €</td>
                      <td>{t(`admin.payment.${booking.payment_status || 'pending'}`, { defaultValue: booking.payment_status || '-' })}</td>
                      <td>{booking.attribution_coupon || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PartnerDashboardPage;
